import { useState } from 'react'
import { motion } from 'framer-motion'
import { Form, Input, Button, message } from 'antd'
import { 
  MailOutlined, 
  PhoneOutlined, 
  EnvironmentOutlined,
  ClockCircleOutlined,
  WechatOutlined,
  LinkedinOutlined,
  GithubOutlined
} from '@ant-design/icons'
import axios from 'axios'

const { TextArea } = Input

const Contact = () => {
  const [form] = Form.useForm()
  const [loading, setLoading] = useState(false)

  const onFinish = async (values) => {
    setLoading(true)
    try {
      const response = await axios.post('/api/contact', values)
      if (response.data.success) {
        message.success('提交成功！我们会尽快联系您')
        form.resetFields()
      }
    } catch (error) {
      message.error('提交失败，请稍后重试')
      console.error('Contact form error:', error)
    } finally {
      setLoading(false)
    }
  }

  const contactInfo = [
    {
      icon: <MailOutlined />,
      title: '邮箱',
      content: 'contact@aivision.com',
      link: 'mailto:contact@aivision.com',
      color: 'from-blue-500 to-cyan-500',
    },
    {
      icon: <PhoneOutlined />,
      title: '电话',
      content: '400-123-4567',
      link: 'tel:400-123-4567',
      color: 'from-green-500 to-teal-500',
    },
    {
      icon: <EnvironmentOutlined />,
      title: '地址',
      content: '北京市朝阳区某某大厦10层',
      color: 'from-purple-500 to-pink-500',
    },
    {
      icon: <ClockCircleOutlined />,
      title: '工作时间',
      content: '周一至周五 9:00-18:00',
      color: 'from-orange-500 to-red-500',
    },
  ]

  const socialMedia = [
    { icon: <WechatOutlined />, name: '微信', color: 'text-green-500' },
    { icon: <LinkedinOutlined />, name: 'LinkedIn', color: 'text-blue-600' },
    { icon: <GithubOutlined />, name: 'GitHub', color: 'text-gray-800' },
  ]

  return (
    <div className="min-h-screen bg-gray-50">
      {/* 页面头部 */}
      <section className="tech-bg py-20 pt-32">
        <div className="container-custom text-center">
          <motion.h1
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="text-4xl md:text-5xl font-bold text-white mb-6"
          >
            联系我们
          </motion.h1>
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
            className="text-xl text-gray-300 max-w-3xl mx-auto"
          >
            我们期待与您交流，为您提供专业的AI视觉解决方案
          </motion.p>
        </div>
      </section>

      {/* 联系信息卡片 */}
      <section className="py-12 -mt-8">
        <div className="container-custom">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {contactInfo.map((info, index) => (
              <motion.div
                key={info.title}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.1 }}
                className="bg-white rounded-xl shadow-lg p-6 hover:shadow-xl transition-shadow"
              >
                <div className={`w-12 h-12 rounded-lg bg-gradient-to-br ${info.color} flex items-center justify-center text-white text-2xl mb-4`}>
                  {info.icon}
                </div>
                <h3 className="text-lg font-bold text-gray-900 mb-2">
                  {info.title}
                </h3>
                {info.link ? (
                  <a
                    href={info.link}
                    className="text-gray-600 hover:text-primary-600 transition-colors"
                  >
                    {info.content}
                  </a>
                ) : (
                  <p className="text-gray-600">{info.content}</p>
                )}
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* 表单和地图 */}
      <section className="py-12">
        <div className="container-custom">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
            {/* 联系表单 */}
            <motion.div
              initial={{ opacity: 0, x: -50 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              className="bg-white rounded-xl shadow-lg p-8"
            >
              <h2 className="text-2xl font-bold text-gray-900 mb-2">
                发送消息
              </h2>
              <p className="text-gray-600 mb-6">
                填写以下表单，我们会在24小时内回复您
              </p>

              <Form
                form={form}
                layout="vertical"
                onFinish={onFinish}
                requiredMark={false}
              >
                <Form.Item
                  label="姓名"
                  name="name"
                  rules={[{ required: true, message: '请输入您的姓名' }]}
                >
                  <Input size="large" placeholder="请输入您的姓名" />
                </Form.Item>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <Form.Item
                    label="公司"
                    name="company"
                    rules={[{ required: true, message: '请输入公司名称' }]}
                  >
                    <Input size="large" placeholder="请输入公司名称" />
                  </Form.Item>

                  <Form.Item
                    label="职位"
                    name="position"
                    rules={[{ required: true, message: '请输入您的职位' }]}
                  >
                    <Input size="large" placeholder="请输入您的职位" />
                  </Form.Item>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <Form.Item
                    label="电话"
                    name="phone"
                    rules={[
                      { required: true, message: '请输入联系电话' },
                      { pattern: /^1\d{10}$/, message: '请输入有效的手机号' }
                    ]}
                  >
                    <Input size="large" placeholder="请输入联系电话" />
                  </Form.Item>

                  <Form.Item
                    label="邮箱"
                    name="email"
                    rules={[
                      { required: true, message: '请输入邮箱地址' },
                      { type: 'email', message: '请输入有效的邮箱地址' }
                    ]}
                  >
                    <Input size="large" placeholder="请输入邮箱地址" />
                  </Form.Item>
                </div>

                <Form.Item
                  label="需求描述"
                  name="message"
                  rules={[{ required: true, message: '请描述您的需求' }]}
                >
                  <TextArea
                    rows={4}
                    placeholder="请详细描述您的需求，以便我们更好地为您服务"
                  />
                </Form.Item>

                <Form.Item>
                  <Button
                    type="primary"
                    size="large"
                    htmlType="submit"
                    loading={loading}
                    className="w-full bg-gradient-to-r from-primary-500 to-primary-600 border-0 h-12 text-lg font-semibold"
                  >
                    提交咨询
                  </Button>
                </Form.Item>
              </Form>
            </motion.div>

            {/* 地图和额外信息 */}
            <motion.div
              initial={{ opacity: 0, x: 50 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              className="space-y-6"
            >
              {/* 地图占位 */}
              <div className="bg-white rounded-xl shadow-lg p-4">
                <div className="aspect-video bg-gray-200 rounded-lg flex items-center justify-center">
                  <div className="text-center text-gray-500">
                    <EnvironmentOutlined className="text-4xl mb-2" />
                    <p>地图加载位置</p>
                    <p className="text-sm">北京市朝阳区某某大厦10层</p>
                  </div>
                </div>
              </div>

              {/* 社交媒体 */}
              <div className="bg-white rounded-xl shadow-lg p-8">
                <h3 className="text-xl font-bold text-gray-900 mb-4">
                  关注我们
                </h3>
                <div className="flex gap-4">
                  {socialMedia.map((social) => (
                    <motion.a
                      key={social.name}
                      href="#"
                      whileHover={{ scale: 1.1 }}
                      whileTap={{ scale: 0.9 }}
                      className={`w-12 h-12 rounded-lg bg-gray-100 flex items-center justify-center ${social.color} text-2xl hover:shadow-lg transition-shadow`}
                      title={social.name}
                    >
                      {social.icon}
                    </motion.a>
                  ))}
                </div>
              </div>

              {/* FAQ */}
              <div className="bg-gradient-to-br from-primary-500 to-primary-700 rounded-xl shadow-lg p-8 text-white">
                <h3 className="text-xl font-bold mb-4">常见问题</h3>
                <div className="space-y-3 text-sm">
                  <div>
                    <p className="font-semibold mb-1">Q: 项目周期一般多长？</p>
                    <p className="text-white/80">A: 根据项目复杂度，一般2-6个月不等</p>
                  </div>
                  <div>
                    <p className="font-semibold mb-1">Q: 是否提供试用服务？</p>
                    <p className="text-white/80">A: 提供POC验证，确保方案可行性</p>
                  </div>
                  <div>
                    <p className="font-semibold mb-1">Q: 如何保证数据安全？</p>
                    <p className="text-white/80">A: 严格的数据加密和访问控制机制</p>
                  </div>
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>
    </div>
  )
}

export default Contact

